/**
 * Offline sync queue for forms (Actions + Cartographies).
 *
 * Approach:
 *  - When the user submits a form offline, the payload is pushed to a queue
 *    stored in localStorage.
 *  - A background flusher tries to POST every queued item when `navigator.onLine`
 *    is true, on load and on the 'online' event.
 *  - The calling page uses `enqueueOrPost()` as a drop-in replacement for axios.post.
 */
import axios from "axios";

const QUEUE_KEY = "offline_sync_queue_v1";

function readQueue() {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function writeQueue(items) {
  localStorage.setItem(QUEUE_KEY, JSON.stringify(items));
  // Notify subscribers (other tabs / indicator)
  window.dispatchEvent(new CustomEvent("offline-queue-changed", { detail: { length: items.length } }));
}

export function getQueueLength() {
  return readQueue().length;
}

export function isOnline() {
  return typeof navigator === "undefined" ? true : navigator.onLine;
}

export async function enqueueOrPost({ method = "post", url, payload, offlineLabel }) {
  if (isOnline()) {
    try {
      const res = await axios({ method, url, data: payload });
      return { ok: true, online: true, data: res.data };
    } catch (e) {
      // If network failure despite navigator.onLine, fall back to queue
      if (!e.response) {
        enqueue({ method, url, payload, offlineLabel });
        return { ok: true, online: false, queued: true };
      }
      throw e;
    }
  }
  enqueue({ method, url, payload, offlineLabel });
  return { ok: true, online: false, queued: true };
}

function enqueue(item) {
  const q = readQueue();
  q.push({
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    created_at: new Date().toISOString(),
    ...item,
  });
  writeQueue(q);
}

export async function flushQueue() {
  if (!isOnline()) return { flushed: 0, remaining: readQueue().length };
  const q = readQueue();
  if (q.length === 0) return { flushed: 0, remaining: 0 };
  const remaining = [];
  let flushed = 0;
  for (const item of q) {
    try {
      await axios({ method: item.method, url: item.url, data: item.payload });
      flushed++;
    } catch (e) {
      // Keep in queue if transient (network). Drop if 4xx (invalid payload) to avoid infinite loop.
      const status = e?.response?.status;
      if (!status || status >= 500) {
        remaining.push(item);
      }
    }
  }
  writeQueue(remaining);
  return { flushed, remaining: remaining.length };
}

export function subscribeQueue(callback) {
  const handler = (e) => callback(e.detail?.length ?? readQueue().length);
  window.addEventListener("offline-queue-changed", handler);
  return () => window.removeEventListener("offline-queue-changed", handler);
}

// Auto-sync on app load and when back online
let _autoInstalled = false;
export function installAutoSync() {
  if (_autoInstalled) return;
  _autoInstalled = true;
  window.addEventListener("online", () => {
    flushQueue();
  });
  // Initial attempt
  setTimeout(() => flushQueue(), 1500);
}

// ---------------------------------------------------------------- cachedGet
// Stale-while-revalidate cache for GET requests. Used by form pages so that
// "Edit" mode keeps working when the user goes offline AFTER having visited
// the item once while online.
const CACHE_KEY_PREFIX = "offline_get_cache_v1::";
const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function _readCache(url) {
  try {
    const raw = localStorage.getItem(CACHE_KEY_PREFIX + url);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.ts !== "number") return null;
    if (Date.now() - parsed.ts > CACHE_TTL_MS) return null;
    return parsed.data;
  } catch {
    return null;
  }
}

function _writeCache(url, data) {
  try {
    localStorage.setItem(CACHE_KEY_PREFIX + url, JSON.stringify({ ts: Date.now(), data }));
    _markFreshSync();
  } catch {
    // localStorage full — silently drop oldest cache entries
    try {
      const keys = Object.keys(localStorage).filter((k) => k.startsWith(CACHE_KEY_PREFIX));
      // Drop half the cache (FIFO based on iteration order, good enough)
      keys.slice(0, Math.ceil(keys.length / 2)).forEach((k) => localStorage.removeItem(k));
      localStorage.setItem(CACHE_KEY_PREFIX + url, JSON.stringify({ ts: Date.now(), data }));
      _markFreshSync();
    } catch {
      /* give up silently */
    }
  }
}

// ----------------------------------------------------- Last sync timestamp
const LAST_SYNC_KEY = "offline_last_sync_v1";

function _markFreshSync() {
  try {
    localStorage.setItem(LAST_SYNC_KEY, String(Date.now()));
    window.dispatchEvent(new CustomEvent("offline-last-sync-changed"));
  } catch { /* ignore */ }
}

export function getLastSyncTime() {
  try {
    const v = localStorage.getItem(LAST_SYNC_KEY);
    return v ? parseInt(v, 10) : null;
  } catch {
    return null;
  }
}

export function subscribeLastSync(callback) {
  const handler = () => callback(getLastSyncTime());
  window.addEventListener("offline-last-sync-changed", handler);
  return () => window.removeEventListener("offline-last-sync-changed", handler);
}

/**
 * Pre-warm the per-item GET cache from a list response. Useful when a listing
 * endpoint returns full item objects: we then populate `urlPrefix/{item.id}`
 * in cache so clicking "Edit" works fully offline (no extra fetch needed).
 */
export function warmItemCache(items, urlPrefix, idKey = "id") {
  if (!Array.isArray(items)) return;
  for (const it of items) {
    if (it && it[idKey]) {
      _writeCache(`${urlPrefix}/${it[idKey]}`, it);
    }
  }
}

/**
 * Prepare-for-field-trip helper: pre-fetches and caches all key listings
 * (and their items) so the user can browse + edit fully offline.
 *
 * Returns: { ok, total, cached, errors, durationMs, sizeKb }
 * Calls onProgress({ step, label, done, total }) at each step.
 */
const FIELD_TRIP_RESOURCES = [
  { key: "actions", label: "Actions", urlSuffix: "/actions", warm: true },
  { key: "rencontres", label: "Rencontres", urlSuffix: "/rencontres", warm: true },
  { key: "personnes", label: "Personnes (terrain)", urlSuffix: "/personnes", warm: true },
  { key: "taches", label: "Tâches", urlSuffix: "/taches", warm: true },
  { key: "cartographies", label: "Cartographies", urlSuffix: "/cartographies", warm: true },
  { key: "fiches", label: "Fiches actions", urlSuffix: "/fiches-action", warm: true },
  { key: "contacts", label: "Carnet de contacts", urlSuffix: "/contacts", warm: true },
  { key: "besoins", label: "Besoins", urlSuffix: "/besoins", warm: true },
];

export async function prepareFieldTripCache({ apiBase, onProgress } = {}) {
  if (!apiBase) throw new Error("apiBase requis");
  if (!isOnline()) {
    const err = new Error("Connexion requise pour préparer le terrain");
    err.offline = true;
    throw err;
  }
  const start = Date.now();
  const total = FIELD_TRIP_RESOURCES.length;
  let cached = 0;
  const errors = [];
  for (let i = 0; i < total; i++) {
    const r = FIELD_TRIP_RESOURCES[i];
    if (onProgress) onProgress({ step: i + 1, label: r.label, done: cached, total });
    const url = `${apiBase}${r.urlSuffix}`;
    try {
      const res = await axios.get(url);
      _writeCache(url, res.data);
      if (r.warm && Array.isArray(res.data)) {
        for (const it of res.data) {
          if (it && it.id) _writeCache(`${url}/${it.id}`, it);
        }
      }
      cached += 1;
    } catch (e) {
      errors.push({ key: r.key, status: e.response?.status, message: e.message });
    }
  }
  if (onProgress) onProgress({ step: total, label: "Terminé", done: cached, total });
  // Estimate cache footprint
  let sizeKb = 0;
  try {
    for (const k of Object.keys(localStorage)) {
      if (k.startsWith(CACHE_KEY_PREFIX)) {
        sizeKb += Math.ceil((localStorage.getItem(k) || "").length / 1024);
      }
    }
  } catch { /* ignore */ }
  return {
    ok: errors.length === 0,
    total,
    cached,
    errors,
    durationMs: Date.now() - start,
    sizeKb,
  };
}

/**
 * Network-first GET with localStorage fallback when offline.
 *
 * Returns: { data, fromCache: bool }
 * Throws: { offline: true } when offline and no cache exists, or any axios HTTP error.
 */
export async function cachedGet(url, axiosConfig) {
  // Helper: detect Service Worker's "fake 503 offline" response
  const isSWOfflineResponse = (e) =>
    e?.response?.status === 503 &&
    (e.response.data?.offline === true || e.response.data === "" || !e.response.data);

  if (isOnline()) {
    try {
      const res = await axios.get(url, axiosConfig);
      _writeCache(url, res.data);
      return { data: res.data, fromCache: false };
    } catch (e) {
      // Real network failure (no response) OR Service Worker offline shim → try cache
      if (!e.response || isSWOfflineResponse(e)) {
        const cached = _readCache(url);
        if (cached !== null) {
          return { data: cached, fromCache: true };
        }
        const err = new Error("Offline and no cache");
        err.offline = true;
        throw err;
      }
      throw e;
    }
  }
  const cached = _readCache(url);
  if (cached !== null) {
    return { data: cached, fromCache: true };
  }
  const err = new Error("Offline and no cache");
  err.offline = true;
  throw err;
}
